import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import UserContext from '../../../context/UserContext';
import AdBannerApi from '../../../api/AdBannerApi';
import { uploadImageFile, resolveImageUrl } from '../../../util/ImageService';
import './adBannerAdmin.css';

const EMPTY_FORM = {
    bannerId: null,
    slotCode: 'main_carousel',
    groupIndex: 0,
    sortOrder: 0,
    title: '',
    imageUrl: '',
    linkUrl: '',
    linkTarget: '_blank',
    isActive: true,
};

const AdBannerAdmin = () => {
    const { user } = useContext(UserContext);
    const [slots, setSlots] = useState([]);
    const [banners, setBanners] = useState([]);
    const [filterSlot, setFilterSlot] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploading, setUploading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const isAdmin = user?.userType === 'admin';

    const load = async () => {
        setLoadError('');
        try {
            const slotRes = await AdBannerApi.getSlots();
            setSlots(slotRes?.slots || []);
        } catch (err) {
            console.error('배너 슬롯 조회 실패:', err);
            setLoadError('슬롯 목록을 불러오지 못했습니다.');
        }

        try {
            const bannerRes = await AdBannerApi.listAdmin(filterSlot);
            setBanners(bannerRes?.banners || []);
        } catch (err) {
            console.error('배너 목록 조회 실패:', err);
            setLoadError((prev) => prev || '배너 목록을 불러오지 못했습니다.');
            setBanners([]);
        }
    };

    useEffect(() => {
        if (user?.isInitiative || !isAdmin) return;
        load();
    }, [user?.isInitiative, isAdmin, filterSlot]);

    if (user?.isInitiative) {
        return <div className="ad-banner-admin ad-banner-admin--loading" />;
    }

    if (!user?.userId) {
        return <Navigate to="/login" replace />;
    }

    if (!isAdmin) {
        return (
            <div className="ad-banner-admin">
                <p className="ad-banner-admin-error">관리자만 접근할 수 있습니다.</p>
            </div>
        );
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleEdit = (banner) => {
        setForm({
            bannerId: banner.bannerId,
            slotCode: banner.slotCode,
            groupIndex: banner.groupIndex,
            sortOrder: banner.sortOrder,
            title: banner.title || '',
            imageUrl: banner.imageUrl,
            linkUrl: banner.linkUrl || '',
            linkTarget: banner.linkTarget || '_blank',
            isActive: banner.isActive,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleReset = () => setForm(EMPTY_FORM);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const result = await uploadImageFile(file, 'banner', 'image');
            setForm((prev) => ({ ...prev, imageUrl: result.imageUrl }));
        } catch (err) {
            alert(err.message || '이미지 업로드 실패');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.imageUrl) return alert('이미지 URL이 필요합니다.');

        const payload = {
            slotCode: form.slotCode,
            groupIndex: Number(form.groupIndex),
            sortOrder: Number(form.sortOrder),
            title: form.title,
            imageUrl: form.imageUrl,
            linkUrl: form.linkUrl,
            linkTarget: form.linkTarget,
            isActive: form.isActive,
        };

        const res = form.bannerId
            ? await AdBannerApi.update(form.bannerId, payload)
            : await AdBannerApi.create(payload);

        if (res?.code === 'success') {
            alert(form.bannerId ? '수정되었습니다.' : '등록되었습니다.');
            handleReset();
            load();
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm('삭제하시겠습니까?')) return;
        const res = await AdBannerApi.remove(bannerId);
        if (res?.code === 'success') load();
    };

    const selectedSlot = slots.find((s) => s.code === form.slotCode);
    const slotLabel = (code) => slots.find((s) => s.code === code)?.label || code;

    return (
        <div className="ad-banner-admin">
            <div className="ad-banner-admin__head">
                <div>
                    <h2>광고 배너 관리</h2>
                    <p>이미지 업로드 후 슬롯별로 배너를 등록·수정합니다.</p>
                </div>
                <select
                    className="ad-banner-admin__filter"
                    value={filterSlot}
                    onChange={(e) => setFilterSlot(e.target.value)}
                >
                    <option value="">전체 슬롯</option>
                    {slots.map((slot) => (
                        <option key={slot.code} value={slot.code}>{slot.label}</option>
                    ))}
                </select>
            </div>

            {loadError && <p className="ad-banner-admin-error">{loadError}</p>}

            <div className="ad-banner-admin__layout">
                <form className="ad-banner-form" onSubmit={handleSubmit}>
                    <div className="ad-banner-form__title">
                        <h3>{form.bannerId ? `배너 수정 #${form.bannerId}` : '배너 등록'}</h3>
                        {form.bannerId && (
                            <button type="button" className="ad-banner-btn ad-banner-btn--ghost" onClick={handleReset}>
                                취소
                            </button>
                        )}
                    </div>

                    <div className="ad-banner-form__fields">
                        <label>
                            슬롯
                            <select name="slotCode" value={form.slotCode} onChange={handleChange}>
                                {slots.map((slot) => (
                                    <option key={slot.code} value={slot.code}>{slot.label}</option>
                                ))}
                            </select>
                        </label>
                        {selectedSlot?.grouped && (
                            <label>
                                그룹
                                <input name="groupIndex" type="number" min="0" value={form.groupIndex} onChange={handleChange} />
                            </label>
                        )}
                        <label>
                            정렬
                            <input name="sortOrder" type="number" min="0" value={form.sortOrder} onChange={handleChange} />
                        </label>
                        <label>
                            제목
                            <input name="title" value={form.title} onChange={handleChange} placeholder="관리용 이름" />
                        </label>
                        <label>
                            링크
                            <input name="linkUrl" value={form.linkUrl} onChange={handleChange} placeholder="https://..." />
                        </label>
                        <label>
                            Target
                            <select name="linkTarget" value={form.linkTarget} onChange={handleChange}>
                                <option value="_blank">새 창</option>
                                <option value="_self">현재 창</option>
                            </select>
                        </label>
                        <label className="ad-banner-form__check">
                            <input name="isActive" type="checkbox" checked={form.isActive} onChange={handleChange} />
                            활성
                        </label>
                    </div>

                    <div className="ad-banner-form__upload">
                        <input
                            name="imageUrl"
                            value={form.imageUrl}
                            onChange={handleChange}
                            placeholder="이미지 URL (업로드 시 자동 입력)"
                        />
                        <label className="ad-banner-btn ad-banner-btn--file">
                            {uploading ? '업로드 중...' : '이미지 선택'}
                            <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} hidden />
                        </label>
                    </div>

                    {form.imageUrl && (
                        <div className="ad-banner-form__preview">
                            <img src={resolveImageUrl(form.imageUrl)} alt="미리보기" />
                        </div>
                    )}

                    <button type="submit" className="ad-banner-btn ad-banner-btn--primary">
                        {form.bannerId ? '수정 저장' : '등록'}
                    </button>
                </form>

                <div className="ad-banner-list">
                    <h3>배너 목록 <span>{banners.length}</span></h3>
                    {banners.length === 0 ? (
                        <p className="ad-banner-list__empty">등록된 배너가 없습니다.</p>
                    ) : (
                        <ul className="ad-banner-cards">
                            {banners.map((banner) => (
                                <li key={banner.bannerId} className="ad-banner-card">
                                    <div className="ad-banner-card__preview">
                                        <img src={resolveImageUrl(banner.imageUrl)} alt={banner.title || '배너'} />
                                    </div>
                                    <div className="ad-banner-card__body">
                                        <div className="ad-banner-card__meta">
                                            <span className="ad-banner-card__id">#{banner.bannerId}</span>
                                            <span className={`ad-banner-card__status${banner.isActive ? '' : ' is-off'}`}>
                                                {banner.isActive ? '활성' : '비활성'}
                                            </span>
                                        </div>
                                        <strong className="ad-banner-card__title">{banner.title || '(제목 없음)'}</strong>
                                        <p className="ad-banner-card__slot">
                                            {slotLabel(banner.slotCode)}
                                            {banner.groupIndex > 0 && ` · 그룹 ${banner.groupIndex}`}
                                            {` · 정렬 ${banner.sortOrder}`}
                                        </p>
                                        <p className="ad-banner-card__link">{banner.linkUrl || '-'}</p>
                                    </div>
                                    <div className="ad-banner-card__actions">
                                        <button type="button" className="ad-banner-btn" onClick={() => handleEdit(banner)}>수정</button>
                                        <button type="button" className="ad-banner-btn ad-banner-btn--danger" onClick={() => handleDelete(banner.bannerId)}>삭제</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdBannerAdmin;
